import React from "react";
import { useFormValidation, FormState } from "ui/hooks/use-form-validation";
import { Modal, FormElement } from "ui/components";
import {
    createMediaFileFromUploadedFile,
    updateMediaFile,
} from "core/services/site/mediafile";
import { MediaFile } from "core/models";
import { useSessionContext } from "ui/context/SessionContext";
import UploadManager from "core/services/uploadManager";
import createPoller from "utils/createPoller";
import { getTask } from "core/services/site/task";

interface Props {
    type: "add" | "edit";
    onClose(): void;
    onSave(): void;
    editableAddon?: MediaFile;
}

const AddonModal: React.FC<Props> = ({
    type,
    onClose,
    onSave,
    editableAddon,
}) => {
    const sessionContext = useSessionContext();
    const [uploadStatus, setUploadStatus] = React.useState<string | null>(null);

    const defaultFormState: FormState = {
        name: {
            valid: true,
            touched: false,
            value: type === "edit" && editableAddon ? editableAddon.name : "",
            errorMsg: "",
            title: "Display name",
            defaultText: "Display name",
            type: "text",
            rules: {
                required: true,
                minLength: 2,
            },
        },
    };
    if (type === "add") {
        defaultFormState.type = {
            valid: true,
            touched: false,
            value: "Intro",
            errorMsg: "",
            title: "Type",
            defaultText: "Type",
            type: "dropdown",
            options: [
                {
                    id: 1,
                    title: "Intro (MP4)",
                    value: "Intro",
                },
                {
                    id: 2,
                    title: "Outro (MP4)",
                    value: "Outro",
                },
                {
                    id: 3,
                    title: "Overlay (PNG)",
                    value: "Overlay",
                },
            ],
            rules: {
                required: true,
            },
        };
        defaultFormState.file = {
            valid: true,
            touched: false,
            value: "",
            title: "File",
            defaultText: "Select file",
            type: "file",
            errorMsg: "",
            rules: {
                required: true,
            },
        };
    }

    const theForm = useFormValidation(defaultFormState);
    const { formState } = theForm;

    const handleSave = async () => {
        try {
            if (!sessionContext.session) {
                throw Error("no session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("no selected project");
            }
            if (type === "edit") {
                if (!editableAddon) {
                    throw Error("no editable addon");
                }
                const result = await updateMediaFile(
                    sessionContext.session.token,
                    editableAddon.id,
                    formState.name.value?.toString() || "",
                );
                if (result.error) {
                    throw new Error(result.error);
                }
                onSave();
            } else {
                if (
                    !(
                        formState.type.value === "Intro" ||
                        formState.type.value === "Outro" ||
                        formState.type.value === "Overlay"
                    )
                ) {
                    throw new Error("incorrect MediaFile type");
                }
                setUploadStatus("Uploading");
                if (!(formState.file.value instanceof File)) {
                    throw new Error("no file found");
                }
                if (!formState.name.value) {
                    throw new Error("no name found");
                }
                const uploadManager = new UploadManager(
                    sessionContext.session.token,
                    sessionContext.selectedProject.id,
                );
                const uploadedFile = await uploadManager.uploadMediaFile(
                    formState.file.value,
                    (progress) => {
                        setUploadStatus(
                            `Uploading ${Math.ceil(progress * 100)}%`,
                        );
                    },
                    formState.type.value,
                    formState.name.value.toString() || "",
                    ".mp4",
                );
                if (uploadedFile.error || !uploadedFile.value) {
                    throw new Error(uploadedFile.error);
                }
                setUploadStatus("Processing file...");
                const createdMediaFile = await createMediaFileFromUploadedFile(
                    sessionContext.session.token,
                    sessionContext.selectedProject.id,
                    0, // Addons are not channel specific, so: 0
                    formState.type.value,
                    `${formState.name.value.toString()}_${Math.ceil(
                        Math.random() * 10000,
                    )}`,
                    formState.name.value.toString(),
                    uploadedFile.value.id,
                );
                if (createdMediaFile.error || !createdMediaFile.value) {
                    throw new Error(createdMediaFile.error);
                }
                let failureCount = 0;
                setUploadStatus("Almost ready...");
                await createPoller(async () => {
                    if (!sessionContext.session) {
                        throw Error("no session");
                    }
                    if (!createdMediaFile.value) {
                        throw Error("no mediaFile created");
                    }
                    try {
                        const taskInfo = await getTask(
                            sessionContext.session.token,
                            createdMediaFile.value.taskID,
                        );
                        if (taskInfo.value?.processing) {
                            return false;
                        }
                        return true;
                    } catch (error) {
                        failureCount += 1;
                        if (failureCount > 4) {
                            // Assume this fragment is broken and stop polling
                            return true;
                        }
                        return false;
                    }
                }, [{ iterations: 500, interval: 2000 }]);
                onSave();
            }
        } catch (err) {
            setUploadStatus(null);
        }
    };

    const getSaveButtonText = (): string => {
        if (uploadStatus) {
            return "Saving addons…";
        }
        if (!uploadStatus && type === "edit") {
            return "Edit";
        }
        return "Create";
    };

    const modalContent = Object.keys(formState).map((fieldKey) => (
        <FormElement
            theme="light"
            defaultText={formState[fieldKey].defaultText}
            type={formState[fieldKey].type || "text"}
            title={formState[fieldKey].title || ""}
            id={fieldKey}
            key={fieldKey}
            fieldState={formState[fieldKey]}
            onChange={(value) => {
                theForm.inputChangeHandler(fieldKey, value);
            }}
            disabled={uploadStatus !== null}
            options={formState[fieldKey].options}
        />
    ));

    return (
        <Modal
            content={modalContent}
            lineColor="amber"
            theme="light"
            onSave={handleSave}
            onClose={onClose}
            pending={uploadStatus ? "saving addon" : undefined}
            saveButtonDisabled={!theForm.formValid || uploadStatus !== null}
            saveButtonText={getSaveButtonText()}
            title={
                type === "add"
                    ? "Add addon"
                    : `Edit Addon: ${editableAddon?.name}`
            }
            icon={type === "add" ? "plus" : "edit"}
        />
    );
};
export default AddonModal;
